import { Card, H3, H6 } from "@sellar-io/components";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { ProfilePicture, BuyerOnly } from "../../../../../components";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
} from "../../../../../components/bestPricePromise/BestPricePromise";
import { AwardBadge } from "../../../../spotlight/components/SharedCardComponents/AwardBadge";
import { productAwardData } from "../../../../spotlight/components/SpotlightProductCard.shared";
import { CategoryImage } from "../../categorySidePanel/CategoryImage";
import { ProductImageType } from "@sellar-io/api-v2/types";
import { ImageSlider } from "../../../../../components/imageSlider/ImageSlider";
import { useMediaQuery } from "usehooks-ts";

export const StorefrontProductCard = ({
  children,
  className,
  topBadge,
  item,
  supplier,
  setBestPricePromiseDialogActive,
  showAwardBadge,
}: {
  children?: ReactNode;
  className?: string;
  topBadge?: ReactNode;
  setBestPricePromiseDialogActive?: (active: boolean) => void;
  item: {
    id: number;
    imageUrl: string | null;
    heroImageUrl: string | null;
    name: string | null;
    description: string | null;
    kind: string | null;
    abv: number | null;
    vegan?: boolean | null;
    lactoseFree?: boolean | null;
    glutenFree?: boolean | null;
    ProductImages?: ProductImageType[] | null;
  };
  supplier:
    | {
        imageUrl: string | null;
        Business: {
          name: string;
        };
      }
    | undefined;
  showAwardBadge?: boolean;
}) => {
  const productHeroImages = item.ProductImages?.map(
    (ProductImage) => ProductImage.url,
  );

  const hasProductHeroImages =
    productHeroImages && productHeroImages.length > 0;

  const productHeroImage = item.ProductImages?.[0]?.url;

  const isDesktop = useMediaQuery("(min-width: 640px)");

  const awardData = productAwardData.find((a) => a.productId === item.id);

  return (
    <Card
      key={item.id}
      className={twMerge("bg-white", "rounded-2xl pt-4", className)}
    >
      <div className="relative">
        {topBadge && (
          <div className="absolute top-0 left-0 z-10 w-full p-2">
            {topBadge}
          </div>
        )}
        {hasProductHeroImages ? (
          <div className="-ml-4 max-w-full md:ml-0">
            <ImageSlider
              images={productHeroImages}
              includeThumbnails={true}
              className="-mx-8 sm:mx-0"
              imageClassName="rounded-none sm:rounded-lg"
            />
          </div>
        ) : (
          <CategoryImage
            imageUrl={
              productHeroImage ?? item.heroImageUrl ?? supplier?.imageUrl ?? ""
            }
            fallback="product-hero"
          />
        )}
      </div>

      <div className="flex flex-col pt-4">
        {isDesktop && (
          <div className="flex flex-row justify-between px-4">
            <ProfilePicture
              size={"2xl"}
              logoUrl={item.imageUrl}
              fallback="product-badge"
              name={item.name}
              className={twMerge(
                "z-1 rounded-full border-2 border-white bg-white",
                hasProductHeroImages && productHeroImages.length > 1
                  ? "-mt-[120px]"
                  : "-mt-16",
              )}
            />
          </div>
        )}
        <div className="mt-6 flex flex-col gap-4 px-4 pb-4">
          <div className="flex flex-col gap-2 md:flex-row md:justify-between">
            <div className="flex gap-4">
              {!isDesktop && (
                <ProfilePicture
                  size={"large"}
                  logoUrl={item.imageUrl}
                  fallback="product-badge"
                  name={item.name}
                  className={twMerge("z-1 rounded-full bg-white")}
                />
              )}
              <div className="flex-col select-text">
                <H3>{item.name}</H3>
                <H6>
                  {item.kind}
                  {" // "}
                  {item.abv}
                </H6>
              </div>
            </div>
            <BuyerOnly>
              <BestPricePromise
                onClick={() => setBestPricePromiseDialogActive?.(true)}
              >
                <BestPricePromiseFullSize
                  toolTipText={"Don't see the best price?"}
                />
              </BestPricePromise>
            </BuyerOnly>
          </div>
          {showAwardBadge && awardData && (
            <div className="flex flex-wrap items-center gap-1">
              {awardData.subCategories.map((sub) => (
                <div key={sub.subCategory} className="flex items-center gap-1">
                  <AwardBadge tier={sub.subCategoryMedal} type={sub.type} />
                  <span className="text-xs text-zinc-500">
                    {sub.subCategory}
                  </span>
                </div>
              ))}
            </div>
          )}
          {item.description && (
            <p className="select-text">{item.description}</p>
          )}
        </div>
      </div>
      {children}
    </Card>
  );
};
